import React from "react";
import SchoolIcon from "@mui/icons-material/School";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonIcon from "@mui/icons-material/Person";

function ClassesModal({ classes, onClose }) {
  const totalStudents = classes.reduce(
    (sum, cls) => sum + cls.sections.reduce((s, sec) => s + sec.studentCount, 0),
    0
  );
  const maxCount = Math.max(
    ...classes.flatMap((cls) => cls.sections.map((s) => s.studentCount)),
    1
  );

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(10px)",
        display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 9999, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 540,
          maxHeight: "85vh",
          background: "linear-gradient(160deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)",
          borderRadius: 18,
          boxShadow: "0 24px 64px rgba(0,0,0,0.6), inset 0 0 0 0.5px rgba(255,255,255,0.12)",
          overflow: "hidden",
          animation: "slideIn 0.3s ease-out",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* HEADER */}
        <div style={{
          padding: "16px 20px",
          borderBottom: "0.5px solid rgba(255,255,255,0.1)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: "rgba(245,158,11,0.18)",
            border: "0.5px solid rgba(245,158,11,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <SchoolIcon sx={{ color: "#F59E0B", fontSize: 18 }} />
          </div>
          <div>
            <h2 style={{ margin: 0, color: "#fff", fontSize: 16, fontWeight: 500 }}>
              Classes & sections
            </h2>
            <p style={{ margin: 0, fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
              Academic structure overview
            </p>
          </div>
          <div style={{
            marginLeft: "auto",
            background: "rgba(245,158,11,0.12)",
            border: "0.5px solid rgba(245,158,11,0.3)",
            borderRadius: 8, padding: "4px 10px",
            fontSize: 11, color: "#F59E0B", fontWeight: 500,
          }}>
            {classes.length} classes
          </div>
        </div>

        {/* BODY */}
        <div style={{
          padding: "16px 20px",
          overflowY: "auto",
          maxHeight: "calc(85vh - 130px)",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          {classes.length === 0 ? (
            <p style={{ color: "rgba(255,255,255,0.5)", textAlign: "center", margin: "24px 0" }}>
              No classes found
            </p>
          ) : (
            classes.map((cls, i) => {
              const classTotal = cls.sections.reduce((s, sec) => s + sec.studentCount, 0);
              return (
                <div key={i} style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "0.5px solid rgba(255,255,255,0.1)",
                  borderRadius: 12, padding: "14px 16px",
                }}>
                  {/* Class header row */}
                  <div style={{
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between", marginBottom: 10,
                  }}>
                    <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>
                      {cls.className}
                    </span>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 5,
                      background: "rgba(245,158,11,0.12)",
                      border: "0.5px solid rgba(245,158,11,0.25)",
                      borderRadius: 20, padding: "3px 10px 3px 6px",
                    }}>
                      <PeopleAltIcon sx={{ fontSize: 12, color: "#F59E0B" }} />
                      <span style={{ fontSize: 11, color: "#F59E0B", fontWeight: 500 }}>
                        {classTotal} students total
                      </span>
                    </div>
                  </div>

                  {/* Sections */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {cls.sections.map((sec, idx) => (
                      <div key={idx} style={{
                        display: "flex", alignItems: "center", gap: 10,
                      }}>
                        {/* Section badge */}
                        <div style={{
                          width: 22, height: 22, borderRadius: 6,
                          background: "rgba(99,179,237,0.15)",
                          border: "0.5px solid rgba(99,179,237,0.3)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <span style={{ fontSize: 10, color: "#63B3ED", fontWeight: 500 }}>
                            {sec.sectionName.charAt(sec.sectionName.length - 1)}
                          </span>
                        </div>

                        {/* Section name */}
                        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", flex: 1 }}>
                          {sec.sectionName}
                        </span>

                        {/* Progress bar + count */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{
                            width: 80, height: 4,
                            background: "rgba(255,255,255,0.08)",
                            borderRadius: 99, overflow: "hidden",
                          }}>
                            <div style={{
                              width: `${Math.round((sec.studentCount / maxCount) * 100)}%`,
                              height: "100%",
                              background: "linear-gradient(90deg,#F59E0B,#FBBF24)",
                              borderRadius: 99,
                            }} />
                          </div>
                          <span style={{
                            fontSize: 13, color: "#fff", fontWeight: 500,
                            minWidth: 20, textAlign: "right",
                          }}>
                            {sec.studentCount}
                          </span>
                          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
                            students
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* FOOTER */}
        <div style={{
          padding: "12px 20px",
          borderTop: "0.5px solid rgba(255,255,255,0.08)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "rgba(0,0,0,0.2)",
        }}>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
            {totalStudents} students across {classes.length} classes
          </span>
          <button
            onClick={onClose}
            style={{
              padding: "7px 20px", borderRadius: 8,
              background: "rgba(245,158,11,0.15)",
              border: "0.5px solid rgba(245,158,11,0.4)",
              color: "#F59E0B", fontSize: 13, fontWeight: 500, cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-16px) scale(.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

export default ClassesModal;